//
// Display home check-in
// --------------------------------------------------
// Runs the four-step check-in on templates/primary/_pages/_check-in.twig. Every field sits in one Freeform
// page; this shows one step at a time, looks up bookings by mobile, and resets the screen for the next
// visitor (the iPad is shared).

import { Formatter } from './helpers/formatter';

window.addEventListener('load', () => {
    const root = document.querySelector('[data-check-in]');
    const form = root?.querySelector('form');

    if (!form) {
        return;
    }

    const STEP_NUMBERS = { appointment: 1, mobile: 2, details: 2, consent: 3, done: 4 };
    const STEPS_WITH_BACK = ['mobile', 'details', 'consent'];
    const STEPS_WITH_IDLE = ['mobile', 'details', 'consent'];

    const MESSAGES = {
        mobile: 'Enter a valid mobile number.',
        email: 'Enter a valid email address.',
        firstName: 'Enter your first name.',
        lastName: 'Enter your last name.',
        failed: 'Sorry, we couldn’t check you in. Please try again, or let our team know you’ve arrived.',
    };

    const idleSeconds = Number(root.dataset.idleSeconds) || 60;
    const idleWarningSeconds = Number(root.dataset.idleWarningSeconds) || 10;
    const doneSeconds = Number(root.dataset.doneSeconds) || 15;

    const steps = root.querySelectorAll('[data-check-in-step]');
    const segments = root.querySelectorAll('[data-check-in-segment]');
    const progress = root.querySelector('[data-check-in-progress]');
    const footnotes = root.querySelectorAll('[data-check-in-footnote]');
    const backButton = root.querySelector('[data-check-in-back]');
    const scroller = root.querySelector('[data-check-in-scroller]');
    const hasAppointmentInput = form.querySelector('[data-check-in-has-appointment]');
    const phoneField = form.querySelector('[data-check-in-field="phone"]');
    const formError = form.querySelector('[data-check-in-form-error]');
    const privacyDialog = root.querySelector('[data-check-in-privacy]');
    const idleDialog = root.querySelector('[data-check-in-idle]');
    const status = root.querySelector('[data-check-in-status]');

    const input = handle => form.querySelector(`[data-check-in-field="${handle}"] input`);

    let currentStep = 'appointment';
    let history = [];
    let firstName = '';
    let wantsBrochure = true;
    let lookup = null;

    function setMessage(handle, error = '') {
        const field = form.querySelector(`[data-check-in-field="${handle}"]`);
        if (!field) {
            return;
        }

        const message = field.querySelector('[data-check-in-message]');
        const text = message.querySelector('[data-check-in-message-text]');

        message.dataset.error = error ? 'true' : 'false';
        message.querySelector('svg').classList.toggle('hidden', !error);
        text.textContent = error || (field.dataset.helper ?? '');

        if (error) {
            input(handle).setAttribute('aria-invalid', 'true');
        } else {
            input(handle).removeAttribute('aria-invalid');
        }
    }

    function clearMessages() {
        form.querySelectorAll('[data-check-in-field]').forEach(field => setMessage(field.dataset.checkInField));
        setFormError('');
    }

    function setFormError(message) {
        formError.textContent = message;
        // The row is always laid out (reserved space); .is-shown only reveals it, so showing it never moves anything
        formError.classList.toggle('is-shown', Boolean(message));
    }

    form.querySelectorAll('[data-check-in-field]').forEach(field => {
        field.dataset.helper = field.querySelector('[data-check-in-message-text]').textContent.trim();
    });

    function isMobile(value) {
        return /^(?:\+?61|0)?4\d{8}$/.test(value.replace(/[\s\-().]/g, ''));
    }

    function isEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    function validate(checks) {
        let firstInvalid = null;

        checks.forEach(([handle, isValid, message]) => {
            setMessage(handle, isValid ? '' : message);
            if (!isValid && !firstInvalid) {
                firstInvalid = input(handle);
            }
        });

        firstInvalid?.focus();

        return !firstInvalid;
    }

    function validateMobile() {
        return validate([['phone', isMobile(input('phone').value), MESSAGES.mobile]]);
    }

    function validateDetails() {
        return validate([
            ['firstName', input('firstName').value.trim() !== '', MESSAGES.firstName],
            ['lastName', input('lastName').value.trim() !== '', MESSAGES.lastName],
            ['phone', isMobile(input('phone').value), MESSAGES.mobile],
            ['email', isEmail(input('email').value.trim()), MESSAGES.email],
        ]);
    }

    // Mobile is one field shown on both S2 screens, so it moves to the screen being shown
    function placePhoneField(step) {
        const slot = form.querySelector(`[data-check-in-phone-slot="${step}"]`);
        if (!slot || slot.contains(phoneField)) {
            return;
        }

        slot.appendChild(phoneField);
        phoneField.dataset.helper = slot.dataset.helper;
        input('phone').setAttribute('enterkeyhint', slot.dataset.enterkeyhint);
        if (phoneField.querySelector('[data-check-in-message]').dataset.error !== 'true') {
            setMessage('phone');
        }
    }

    function setDetailsVariant(variant) {
        form.querySelectorAll('[data-check-in-details-heading], [data-check-in-details-body]').forEach(element => {
            element.textContent = variant === 'not-found' ? element.dataset.notFound : element.dataset.walkIn;
        });
    }

    function setFirstName(name) {
        firstName = name.trim();

        root.querySelector('#check-in-consent-heading').textContent = firstName ? `Welcome, ${firstName}.` : 'Welcome.';
        root.querySelector('#check-in-done-heading').textContent = firstName ? `Thanks ${firstName}, you’re checked in.` : 'Thanks, you’re checked in.';
    }

    function updateProgress(step) {
        const number = STEP_NUMBERS[step];
        const isDone = step === 'done';

        segments.forEach((segment, index) => {
            const position = index + 1;
            segment.dataset.state = isDone || position < number ? 'done' : position === number ? 'current' : 'upcoming';
        });

        progress.setAttribute('aria-valuenow', String(number));
    }

    function showStep(step, { addToHistory = true, focus = true, direction = 'forward' } = {}) {
        if (addToHistory && currentStep !== step) {
            history.push(currentStep);
        }

        currentStep = step;
        root.dataset.checkInStepCurrent = step;
        // Presentation hook for the step transition CSS; set before the step shows (goBack passes "back")
        root.dataset.checkInDirection = direction;

        // Presentation: the S1 threshold (arch band + lockup, .is-threshold in the markup) plays on load and replays
        // on every reset; leaving S1 retires it, so Back into S1 gets the plain slide. Re-triggered before S1 shows.
        if (step !== 'appointment') {
            root.classList.remove('is-threshold');
        } else if (direction === 'reset') {
            root.classList.remove('is-threshold');
            void root.offsetWidth;
            root.classList.add('is-threshold');
        }

        placePhoneField(step);

        steps.forEach(section => {
            section.hidden = section.dataset.checkInStep !== step;
        });

        footnotes.forEach(footnote => {
            footnote.hidden = footnote.dataset.checkInFootnote !== step;
        });

        // The arrival mark draws whenever the done step shows (it is hidden, so the animation starts with the step)
        root.querySelector('[data-check-in-arrival]')?.classList.toggle('is-arriving', step === 'done');

        backButton.classList.toggle('invisible', !STEPS_WITH_BACK.includes(step));
        updateProgress(step);
        scroller.scrollTop = 0;

        if (focus) {
            focusStep(step);
        }

        restartIdleTimer();
    }

    // Input screens focus the first empty field so the keyboard is ready; others focus the heading for screen readers
    function focusStep(step) {
        const section = root.querySelector(`[data-check-in-step="${step}"]`);
        const inputs = Array.from(section.querySelectorAll('[data-check-in-field] input'));
        const target = inputs.find(field => !field.value.trim()) ?? section.querySelector('h1, h2');

        target?.focus({ preventScroll: true });
    }

    function goBack() {
        const previous = history.pop();
        if (previous) {
            clearMessages();
            showStep(previous, { addToHistory: false, direction: 'back' });
        }
    }

    root.querySelectorAll('[data-check-in-appointment]').forEach(tile => {
        tile.addEventListener('click', () => {
            const hasAppointment = tile.dataset.checkInAppointment;

            hasAppointmentInput.value = hasAppointment;
            clearMessages();

            if (hasAppointment === 'yes') {
                showStep('mobile');
            } else {
                setDetailsVariant('walk-in');
                showStep('details');
            }
        });
    });

    function setLoading(button, isLoading) {
        button.disabled = isLoading;
        button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        // Announce the wait ("Finding your booking", "Checking in") through the polite live region
        if (status) {
            status.textContent = isLoading ? (button.querySelector('.ed-btn__label--busy')?.textContent.trim() ?? '') : '';
        }
        button.querySelector('[data-check-in-loading]')?.classList.toggle('hidden', !isLoading);
        button.querySelector('[data-check-in-loading]')?.classList.toggle('flex', isLoading);
        button.querySelector('[data-check-in-label]')?.classList.toggle('invisible', isLoading);
    }

    async function findBooking(phone) {
        lookup?.abort();
        lookup = new AbortController();

        const body = new FormData();
        body.set('phone', phone);
        body.set(window.csrfTokenName, window.csrfTokenValue);

        const response = await fetch(root.dataset.lookupUrl, {
            method: 'POST',
            body,
            headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            signal: lookup.signal,
        });

        if (!response.ok) {
            throw new Error(`Booking lookup failed with ${response.status}`);
        }

        return response.json();
    }

    async function submitMobile(button) {
        if (!validateMobile()) {
            return;
        }

        setLoading(button, true);

        let booking = { found: false };
        try {
            booking = await findBooking(input('phone').value);
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }
            // Visitors can still check in by entering their details
            console.warn('[Check-in]', error);
        } finally {
            setLoading(button, false);
        }

        // Stale result, e.g. the screen was reset mid-lookup
        if (currentStep !== 'mobile') {
            return;
        }

        // A found booking supplies the name and email on submit, so anything typed earlier is cleared
        ['firstName', 'lastName', 'email'].forEach(handle => {
            input(handle).value = '';
            setMessage(handle);
        });

        if (booking.found) {
            setFirstName(booking.firstName ?? '');
            showStep('consent');
        } else {
            setDetailsVariant('not-found');
            showStep('details');
        }
    }

    function submitDetails() {
        if (validateDetails()) {
            setFirstName(input('firstName').value);
            showStep('consent');
        }
    }

    root.querySelectorAll('[data-check-in-next]').forEach(button => {
        button.addEventListener('click', () => {
            if (button.dataset.checkInNext === 'mobile') {
                submitMobile(button);
            } else {
                submitDetails();
            }
        });
    });

    form.addEventListener('input', event => {
        const field = event.target.closest('[data-check-in-field]');
        if (field) {
            setMessage(field.dataset.checkInField);
        }
    });

    // The keyboard's Next/Go key moves through the step's fields, then acts as the step's button.
    // It must never submit the whole form early.
    form.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT' || event.target.type === 'checkbox') {
            return;
        }

        event.preventDefault();

        const section = event.target.closest('[data-check-in-step]');
        const inputs = Array.from(section.querySelectorAll('[data-check-in-field] input'));
        const next = inputs[inputs.indexOf(event.target) + 1];

        if (next) {
            next.focus();
        } else {
            section.querySelector('[data-check-in-next]')?.click();
        }
    });

    backButton.addEventListener('click', goBack);

    const submitButton = form.querySelector('[data-check-in-submit]');

    // Freeform puts its captcha at the end of the form, outside every step
    const captcha = form.querySelector('[data-freeform-turnstile-container], [data-freeform-recaptcha-container], [data-freeform-hcaptcha-container]');
    if (captcha) {
        form.querySelector('[data-check-in-captcha-slot]').appendChild(captcha);
    }

    const disableAutoScroll = () => form.freeform?.setOption?.('autoScroll', false);
    form.addEventListener('freeform-ready', disableAutoScroll);
    disableAutoScroll();

    form.addEventListener('freeform-on-submit', event => {
        if (currentStep !== 'consent') {
            event.preventDefault();
        }
    });

    form.addEventListener('freeform-ajax-before-submit', event => {
        clearMessages();
        setLoading(submitButton, true);

        // Freeform resets the form on success, so keep what S4 needs
        wantsBrochure = form.querySelector('[data-check-in-consent="brochureOptIn"]').checked;

        form.querySelectorAll('[data-check-in-field]').forEach(field => {
            const fieldInput = field.querySelector('input');
            event.data.delete(fieldInput.name);
            event.data.set(field.dataset.checkInField, fieldInput.value.trim());
        });

        // Stored in the same format as the site's other forms
        event.data.set('phone', Formatter.formatMobileNumber(input('phone').value.trim()));
    });

    const clearSubmitLoading = () => setLoading(submitButton, false);
    form.addEventListener('freeform-ajax-after-submit', clearSubmitLoading);
    form.addEventListener('freeform-after-failed-submit', clearSubmitLoading);
    form.addEventListener('freeform-ajax-error', clearSubmitLoading);

    form.addEventListener('freeform-render-field-errors', event => {
        event.preventDefault();

        const errors = event.errors || {};
        const detailHandles = ['firstName', 'lastName', 'phone', 'email'].filter(handle => errors[handle]);

        if (!detailHandles.length) {
            setFormError(MESSAGES.failed);
            return;
        }

        setDetailsVariant(hasAppointmentInput.value === 'yes' ? 'not-found' : 'walk-in');
        showStep('details', { focus: false });
        detailHandles.forEach(handle => setMessage(handle, [].concat(errors[handle])[0]));
        input(detailHandles[0]).focus();
    });

    form.addEventListener('freeform-render-form-errors', event => {
        event.preventDefault();
        setFormError(MESSAGES.failed);
    });

    form.addEventListener('freeform-render-success', event => event.preventDefault());

    form.addEventListener('freeform-ajax-success', () => {

        const doneBody = root.querySelector('[data-check-in-done-body]');
        doneBody.textContent = wantsBrochure ? doneBody.dataset.brochure : doneBody.dataset.noBrochure;

        showStep('done', { addToHistory: false });
        history = [];
        startDoneCountdown();
    });

    root.querySelectorAll('[data-check-in-privacy-open]').forEach(link => {
        link.addEventListener('click', event => {
            // The link sits inside the checkbox's label, so stop it toggling the checkbox
            event.preventDefault();
            event.stopPropagation();
            cancelClosing(privacyDialog);
            privacyReturn = link;
            privacyDialog.showModal();
            privacyDialog.querySelector('[data-check-in-dialog-close]:not(div)')?.focus({ focusVisible: false });
        });
    });

    // Closing plays the sheet's reverse motion first (.is-closing), then closes the dialog: WebKit drops a closed
    // dialog at once. Instant under reduced motion. Focus return and Escape are the dialog's own.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const closeTimers = new WeakMap();
    // Presentation/focus: the link that opened the privacy sheet gets focus back when it closes (WebKit does not
    // restore it, and a tapped button there was never focused, so the dialog has nothing to return to)
    let privacyReturn = null;

    function returnFocus(dialog) {
        if (dialog === privacyDialog && privacyReturn && document.activeElement !== privacyReturn) {
            privacyReturn.focus({ preventScroll: true, focusVisible: false });
        }
    }

    function cancelClosing(dialog) {
        clearTimeout(closeTimers.get(dialog));
        dialog.classList.remove('is-closing');
    }

    function closeSheet(dialog) {
        if (!dialog.open || dialog.classList.contains('is-closing')) {
            return;
        }

        if (reducedMotion.matches) {
            dialog.close();
            returnFocus(dialog);
            return;
        }

        dialog.classList.add('is-closing');
        closeTimers.set(dialog, setTimeout(() => {
            dialog.close();
            dialog.classList.remove('is-closing');
            returnFocus(dialog);
        }, 400));
    }

    privacyDialog.querySelectorAll('[data-check-in-dialog-close]').forEach(close => {
        close.addEventListener('click', () => closeSheet(privacyDialog));
    });

    // Escape closes the lightbox with the same motion
    privacyDialog.addEventListener('cancel', event => {
        event.preventDefault();
        closeSheet(privacyDialog);
    });

    // Programmatic focus only shows a ring when the last input was the keyboard (presentation hook for the sheets)
    root.addEventListener('keydown', () => root.setAttribute('data-check-in-keys', ''), true);
    root.addEventListener('pointerdown', () => root.removeAttribute('data-check-in-keys'), true);

    let idleTimer = null;
    let warningTimer = null;
    let doneTimer = null;

    function stopTimers() {
        clearTimeout(idleTimer);
        clearInterval(warningTimer);
        clearInterval(doneTimer);
    }

    function restartIdleTimer() {
        clearTimeout(idleTimer);

        if (!STEPS_WITH_IDLE.includes(currentStep) || idleDialog.open) {
            return;
        }

        idleTimer = setTimeout(showIdlePrompt, idleSeconds * 1000);
    }

    // Presentation: restart a .ed-kcountdown hairline so it drains over the same seconds as the timer beside it
    function runHairline(element, seconds) {
        if (!element) {
            return;
        }

        element.classList.remove('is-running');
        element.style.setProperty('--ed-kiosk-countdown-dur', `${seconds}s`);
        void element.offsetWidth;
        element.classList.add('is-running');
    }

    function stopHairlines() {
        root.querySelectorAll('[data-check-in-hairline]').forEach(element => element.classList.remove('is-running'));
    }

    function countdown(seconds, element, onFinish) {
        let remaining = seconds;
        element.textContent = String(remaining);

        return setInterval(() => {
            remaining -= 1;
            element.textContent = String(Math.max(remaining, 0));

            if (remaining <= 0) {
                onFinish();
            }
        }, 1000);
    }

    function showIdlePrompt() {
        if (privacyDialog.open) {
            privacyDialog.close();
        }

        cancelClosing(idleDialog);
        idleDialog.showModal();
        idleDialog.querySelector('[data-check-in-idle-stay]').focus({ focusVisible: false });
        warningTimer = countdown(idleWarningSeconds, idleDialog.querySelector('[data-check-in-idle-countdown]'), restart);
        runHairline(idleDialog.querySelector('[data-check-in-hairline]'), idleWarningSeconds);
    }

    idleDialog.querySelector('[data-check-in-idle-stay]').addEventListener('click', () => {
        clearInterval(warningTimer);
        stopHairlines();
        closeSheet(idleDialog);
        restartIdleTimer();
    });

    // Escape would leave the countdown running without the prompt
    idleDialog.addEventListener('cancel', event => event.preventDefault());

    function startDoneCountdown() {
        clearInterval(doneTimer);
        doneTimer = countdown(doneSeconds, root.querySelector('[data-check-in-countdown]'), restart);
        runHairline(root.querySelector('[data-check-in-footnote="done"] [data-check-in-hairline]'), doneSeconds);
    }

    // New random names for each visitor, as the page isn't reloaded between them
    function renameFields() {
        form.querySelectorAll('[data-check-in-field]').forEach(field => {
            const fieldInput = field.querySelector('input');
            const id = `f${Math.random().toString(36).slice(2, 14)}`;

            fieldInput.id = id;
            fieldInput.name = id;
            fieldInput.setAttribute('aria-describedby', `${id}-message`);
            field.querySelector('label').htmlFor = id;
            field.querySelector('[data-check-in-message]').id = `${id}-message`;
        });
    }

    function restart() {
        stopTimers();
        stopHairlines();
        lookup?.abort();

        [privacyDialog, idleDialog].forEach(dialog => {
            cancelClosing(dialog);
            dialog.open && dialog.close();
        });

        form.reset();
        renameFields();
        hasAppointmentInput.value = '';
        history = [];
        setFirstName('');
        setDetailsVariant('walk-in');
        clearMessages();
        root.querySelectorAll('[data-check-in-next], [data-check-in-submit]').forEach(button => setLoading(button, false));

        // "reset" fades S1 in (no sideways slide), the screen clearing for the next visitor
        showStep('appointment', { addToHistory: false, focus: false, direction: 'reset' });
        document.activeElement?.blur();
    }

    root.querySelectorAll('[data-check-in-restart]').forEach(button => button.addEventListener('click', restart));

    ['pointerdown', 'keydown', 'input'].forEach(type => {
        root.addEventListener(type, restartIdleTimer, { passive: true });
    });

    // Staff tap the logo three times to enter full screen, so visitors can't leave the page on the iPad
    const logo = root.querySelector('[data-check-in-fullscreen]');
    let logoTaps = 0;
    let logoTapTimer = null;

    function isFullscreen() {
        return Boolean(document.fullscreenElement || document.webkitFullscreenElement);
    }

    function toggleFullscreen() {
        const page = document.documentElement;

        if (isFullscreen()) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        } else {
            // Browsers only allow an orientation lock in full screen, and iPad Safari ignores it
            Promise.resolve((page.requestFullscreen || page.webkitRequestFullscreen)?.call(page))
                .then(() => screen.orientation?.lock?.('portrait'))
                .catch(() => {});
        }
    }

    // Touch devices turned sideways get a prompt to turn back to portrait instead of the check-in
    const touchQuery = window.matchMedia('(pointer: coarse)');

    function updateOrientation() {
        const type = screen.orientation?.type ?? (Math.abs(window.orientation) === 90 ? 'landscape' : 'portrait');

        root.toggleAttribute('data-check-in-landscape', touchQuery.matches && type.startsWith('landscape'));
    }

    screen.orientation?.addEventListener('change', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    updateOrientation();

    logo?.addEventListener('click', () => {
        logoTaps += 1;
        clearTimeout(logoTapTimer);
        logoTapTimer = setTimeout(() => {
            logoTaps = 0;
        }, 600);

        if (logoTaps === 3) {
            logoTaps = 0;
            toggleFullscreen();
        }
    });

    // Safari can restore this page from its back/forward cache with the last visitor's answers
    window.addEventListener('pageshow', event => {
        if (event.persisted) {
            restart();
        }
    });

    // Size the screen to the area the keyboard leaves, so the centred content moves up above it
    const viewport = window.visualViewport;
    const KEYBOARD_MIN_HEIGHT = 150;

    if (viewport) {
        const fitToViewport = () => {
            const keyboardOpen = window.innerHeight - viewport.height * viewport.scale > KEYBOARD_MIN_HEIGHT;

            root.style.setProperty('--check-in-height', `${viewport.height}px`);
            root.toggleAttribute('data-check-in-keyboard', keyboardOpen);
            window.scrollTo(0, 0);
        };

        viewport.addEventListener('resize', fitToViewport);
        fitToViewport();
    }

    // Scroll the whole field into view above the sticky NEXT bar
    form.addEventListener('focusin', event => {
        if (event.target.tagName !== 'INPUT' || event.target.type === 'checkbox') {
            return;
        }

        setTimeout(() => {
            const field = event.target.closest('[data-check-in-field]') ?? event.target;
            const nextBar = event.target.closest('[data-check-in-step]')?.querySelector('[data-check-in-next-bar]');
            const scrollerBox = scroller.getBoundingClientRect();
            const fieldBox = field.getBoundingClientRect();
            const visibleBottom = scrollerBox.bottom - (nextBar?.offsetHeight ?? 0);

            if (fieldBox.top < scrollerBox.top || fieldBox.height > visibleBottom - scrollerBox.top) {
                scroller.scrollTop += fieldBox.top - scrollerBox.top;
            } else if (fieldBox.bottom > visibleBottom) {
                scroller.scrollTop += fieldBox.bottom - visibleBottom;
            }
        }, 300);
    });

    showStep('appointment', { addToHistory: false, focus: false });
});
